import { z } from "zod";

/**
 * POST_AUTH_FALLBACK is where an authenticated visitor lands when no usable
 * `return_to` accompanies the auth route they opened — the studio, the app's home
 * surface. It is also the fallback for a `return_to` that fails validation, so a
 * hostile or self-referential value degrades to a safe destination rather than
 * being followed.
 */
export const POST_AUTH_FALLBACK = "/studio";

/**
 * AUTH_PATHNAME_SEGMENTS are the first path segments the Ory self-service surface
 * owns. A `return_to` pointing back into one of them is refused: sending a
 * signed-in user from an auth page to another auth page is the shape of a redirect
 * cycle (`/login?return_to=/login` re-enters the same flow forever), and none of
 * these pages is a meaningful post-authentication landing.
 */
export const AUTH_PATHNAME_SEGMENTS: ReadonlySet<string> = new Set([
  "error",
  "login",
  "logout",
  "oauth2",
  "recovery",
  "registration",
  "verification",
]);

/**
 * URL_STRIPPED_CHARACTERS are the three characters the URL parser removes from a
 * value before resolving it. They are removed here first so the string validated
 * below is the one the browser would actually navigate to: `"/\tevil.com"`
 * collapses to `"//evil.com"`, a protocol-relative jump to another origin, and must
 * be judged as such rather than passing as an innocuous path.
 */
const URL_STRIPPED_CHARACTERS = ["\t", "\n", "\r"];

/**
 * stripUrlIgnoredCharacters removes the characters the URL parser drops, so a
 * destination is validated in its resolved form rather than its written form.
 */
const stripUrlIgnoredCharacters = (value: string): string => {
  let stripped = value;
  for (const character of URL_STRIPPED_CHARACTERS) {
    stripped = stripped.split(character).join("");
  }

  return stripped;
};

/**
 * firstSegment reads the leading path segment of a site-relative path, ignoring the
 * query and fragment — `"/login?flow=abc"` yields `"login"`. Used to tell an auth
 * destination from an app destination.
 */
const firstSegment = (path: string): string => path.split(/[?#]/, 1)[0]?.split("/", 2)[1] ?? "";

/**
 * returnToSchema accepts only a site-relative path this app is willing to navigate
 * to after authentication. Every other shape — an absolute URL, a
 * protocol-relative `//host` jump, the backslash variant browsers normalize to the
 * same thing, or a path back into the auth surface — is rejected so the caller falls
 * back to {@link POST_AUTH_FALLBACK}.
 *
 * Absolute URLs are refused outright rather than origin-checked: every producer in
 * this app supplies a relative href (TanStack Router's `location.href` is
 * path + search + hash), so accepting an absolute form would only widen the surface
 * for a `return_to` an attacker planted in a link.
 */
const returnToSchema = z
  .string()
  .transform((value) => stripUrlIgnoredCharacters(value).trim())
  .refine((value) => value.startsWith("/"))
  .refine((value) => !value.startsWith("//") && !value.startsWith("/\\"))
  .refine((value) => !AUTH_PATHNAME_SEGMENTS.has(firstSegment(value)));

/**
 * safeReturnTo resolves the post-authentication destination from an untrusted
 * `return_to` search param, falling back to {@link POST_AUTH_FALLBACK} whenever the
 * value is absent or unusable.
 *
 * It is the single seam every post-auth navigation goes through — the `_auth` guest
 * guard, the `_app` gate that mints `return_to`, and the flow hook's
 * `defaultRedirect` — so the open-redirect refusal and the auth-page self-loop
 * refusal are stated once and hold everywhere.
 */
export const safeReturnTo = (raw: string | undefined): string => {
  if (raw === undefined) {
    return POST_AUTH_FALLBACK;
  }

  const parsed = returnToSchema.safeParse(raw);

  return parsed.success ? parsed.data : POST_AUTH_FALLBACK;
};
